/**
 * Aqualumen product rail: collection tabs (click, arrow keys, Home/End) and previous/next
 * buttons for the horizontal product carousels (aq-featured-collection).
 * When the tabs overflow (phones), the tab list gets data-more-start / data-more-end so
 * aq-home.css can fade the edge that hides more tabs, and the chosen tab scrolls into view.
 */
class AqRail extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    for (const tab of this.querySelectorAll('[data-rail-tab]')) {
      tab.addEventListener('click', () => this.#select(/** @type {HTMLElement} */ (tab)), { signal });
      tab.addEventListener('keydown', (event) => this.#onTabKey(/** @type {KeyboardEvent} */ (event)), { signal });
    }
    this.querySelector('[data-rail-prev]')?.addEventListener('click', () => this.#scroll(-1), { signal });
    this.querySelector('[data-rail-next]')?.addEventListener('click', () => this.#scroll(1), { signal });
    for (const track of this.querySelectorAll('[data-rail-track]')) {
      track.addEventListener('scroll', () => this.#updateButtons(), { signal, passive: true });
    }
    this.#updateButtons();

    const tabList = this.#tabList;
    if (tabList) {
      tabList.addEventListener('scroll', () => this.#updateTabEdges(), { signal, passive: true });
      this.#resizeObserver = new ResizeObserver(() => this.#updateTabEdges());
      this.#resizeObserver.observe(tabList);
      this.#updateTabEdges();
    }
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  get #tabList() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[role="tablist"]'));
  }

  #updateTabEdges() {
    const list = this.#tabList;
    if (!list) return;
    const max = list.scrollWidth - list.clientWidth;
    list.toggleAttribute('data-more-start', max > 2 && list.scrollLeft > 2);
    list.toggleAttribute('data-more-end', max > 2 && list.scrollLeft < max - 2);
  }

  get #track() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-rail-track]:not([hidden])'));
  }

  /** @param {HTMLElement} tab */
  #select(tab) {
    for (const other of this.querySelectorAll('[data-rail-tab]')) {
      const selected = other === tab;
      other.setAttribute('aria-selected', String(selected));
      other.setAttribute('tabindex', selected ? '0' : '-1');
      const panel = document.getElementById(other.getAttribute('aria-controls') ?? '');
      if (panel) panel.hidden = !selected;
    }
    const list = this.#tabList;
    if (list && list.scrollWidth > list.clientWidth) {
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const start = tab.getBoundingClientRect().left - list.getBoundingClientRect().left + list.scrollLeft;
      const end = start + tab.offsetWidth;
      // Keep the chosen tab clear of the faded edges (56px)
      let left = list.scrollLeft;
      if (start - 56 < left) left = Math.max(0, start - 56);
      else if (end + 56 > left + list.clientWidth) left = end + 56 - list.clientWidth;
      list.scrollTo({ left, behavior: reduceMotion ? 'auto' : 'smooth' });
    }
    this.#updateButtons();
  }

  /** @param {KeyboardEvent} event */
  #onTabKey(event) {
    const tabs = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-rail-tab]')]);
    const index = tabs.indexOf(/** @type {HTMLElement} */ (event.currentTarget));
    if (index < 0) return;
    let next = -1;
    if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
    else if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    if (next < 0) return;
    event.preventDefault();
    // preventScroll: the browser's own focus scroll would cancel the smooth scroll in #select
    tabs[next].focus({ preventScroll: true });
    this.#select(tabs[next]);
  }

  /** @param {number} direction */
  #scroll(direction) {
    const track = this.#track;
    const card = track?.firstElementChild;
    if (!track || !(card instanceof HTMLElement)) return;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollBy({ left: direction * (card.offsetWidth + gap), behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  #updateButtons() {
    const track = this.#track;
    const prev = this.querySelector('[data-rail-prev]');
    const next = this.querySelector('[data-rail-next]');
    if (!track || !prev || !next) return;
    const max = track.scrollWidth - track.clientWidth - 2;
    prev.toggleAttribute('disabled', track.scrollLeft <= 2);
    next.toggleAttribute('disabled', track.scrollLeft >= max);
  }
}

if (!customElements.get('aq-rail')) customElements.define('aq-rail', AqRail);
